import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  Divider,
  IconButton,
} from '@mui/material';
import { MdAdd, MdDelete } from 'react-icons/md';
import theme from '../theme';
import {
  fetchComplianceSettings,
  updateComplianceSettings,
} from '../../services/clientReportsApi';

const COLORS = theme.colors;

const METHOD_OPTIONS = [
  { value: 'direct_observation', label: 'Direct observation' },
  { value: 'atp', label: 'ATP' },
  { value: 'fluorescent_marker', label: 'Fluorescent marker' },
  { value: 'agar_slide', label: 'Agar slide' },
  { value: 'swab_culture', label: 'Swab culture' },
];

const TIER_OPTIONS = ['general', 'icu', 'ot', 'transplant', 'other'];

/**
 * Tenant compliance settings: retention, coverage threshold, device certs, verification thresholds.
 */
export default function ComplianceSettingsPanel({ email, adminView }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);

  const load = async () => {
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchComplianceSettings(email);
      if (data?.success) setSettings(data.settings);
      else setError(data?.message || 'Unable to load compliance settings');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load compliance settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const updateField = (path, value) => {
    setSettings((prev) => {
      if (!prev) return prev;
      if (path.startsWith('deviceCerts.')) {
        const key = path.split('.')[1];
        return {
          ...prev,
          deviceCerts: { ...prev.deviceCerts, [key]: value },
        };
      }
      return { ...prev, [path]: value };
    });
  };

  const updateThreshold = (index, field, value) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const list = [...(prev.verificationThresholds || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, verificationThresholds: list };
    });
  };

  const addThreshold = () => {
    setSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        verificationThresholds: [
          ...(prev.verificationThresholds || []),
          {
            riskTier: 'general',
            method: 'atp',
            instrumentId: 'default',
            passMax: '',
            passMin: '',
            unit: 'RLU',
            sourceCitation: '',
            effectiveFrom: new Date().toISOString(),
          },
        ],
      };
    });
  };

  const removeThreshold = (index) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const list = [...(prev.verificationThresholds || [])];
      list.splice(index, 1);
      return { ...prev, verificationThresholds: list };
    });
  };

  const handleSave = async () => {
    if (adminView || !settings) return;
    const missingCitation = (settings.verificationThresholds || []).find(
      (t) => !String(t.sourceCitation || '').trim()
    );
    if (missingCitation) {
      setError('Each verification threshold requires a source citation.');
      setSuccess('');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        auditRetentionYears: Number(settings.auditRetentionYears),
        coverageAccuracyThreshold: Number(settings.coverageAccuracyThreshold),
        overdueRoomDays: Number(settings.overdueRoomDays),
        followUpTurnoverMinutes: Number(settings.followUpTurnoverMinutes),
        deviceCerts: settings.deviceCerts,
        verificationThresholds: (settings.verificationThresholds || []).map((t) => ({
          riskTier: t.riskTier,
          method: t.method,
          instrumentId: t.instrumentId || 'default',
          passMax: t.passMax === '' || t.passMax == null ? null : Number(t.passMax),
          passMin: t.passMin === '' || t.passMin == null ? null : Number(t.passMin),
          unit: t.unit || '',
          sourceCitation: t.sourceCitation || '',
          effectiveFrom: t.effectiveFrom || new Date().toISOString(),
        })),
      };
      const data = await updateComplianceSettings(email, payload);
      if (data?.success) {
        setSettings(data.settings);
        setSuccess('Hygiene reporting settings saved.');
      } else {
        setError(data?.message || 'Save failed');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: COLORS.brandPrimary }} />
      </Box>
    );
  }

  if (!settings) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  const certs = settings.deviceCerts || {};

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${COLORS.borderDefault}`,
        bgcolor: '#fff',
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, mb: 0.5 }}>
        Hygiene reporting settings
      </Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 2, lineHeight: 1.5 }}>
        Thresholds are tenant-configurable and never universal. Every evidence pack prints the
        threshold used and its cited source. Device certificate numbers appear on evidence PDF
        footers only when provided. This panel tunes disinfection / hygiene reporting — it is not
        a document-control, accreditation, zone/asset master, protocol CMS, or delivery
        (webhook/SFTP) module.
      </Typography>
      <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
        Audit retention (minimum 3 years, default 7) governs how far back evidence packs and cycles
        are listed, and blocks deletion of robot history still inside that window. Records within
        retention cannot be removed by any role.
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      ) : null}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          size="small"
          type="number"
          label="Audit retention (years)"
          helperText="Min 3, default 7 — evidence list window; within-retention deletes are refused"
          value={settings.auditRetentionYears ?? 7}
          disabled={adminView}
          onChange={(e) => updateField('auditRetentionYears', e.target.value)}
          inputProps={{ min: 3 }}
        />
        <TextField
          size="small"
          type="number"
          label="Coverage accuracy threshold (%)"
          helperText="Proxy adequacy % for object coverage (not a radiometric dose target)"
          value={settings.coverageAccuracyThreshold ?? 80}
          disabled={adminView}
          onChange={(e) => updateField('coverageAccuracyThreshold', e.target.value)}
          inputProps={{ min: 1, max: 100 }}
        />
        <TextField
          size="small"
          type="number"
          label="Overdue room days"
          value={settings.overdueRoomDays ?? 14}
          disabled={adminView}
          onChange={(e) => updateField('overdueRoomDays', e.target.value)}
          inputProps={{ min: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label="Follow-up turnover SLA (minutes)"
          helperText="Manual wipe due after cycle end (default 60)"
          value={settings.followUpTurnoverMinutes ?? 60}
          disabled={adminView}
          onChange={(e) => updateField('followUpTurnoverMinutes', e.target.value)}
          inputProps={{ min: 1 }}
        />
      </Box>

      <Divider sx={{ my: 2.5 }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5, color: COLORS.textPrimary }}>
        Device certificate placeholders (report footer)
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {[
          ['biologicalEfficacy', 'Biological efficacy cert #'],
          ['iec62471', 'IEC 62471'],
          ['iecUl60335', 'IEC/UL 60335 / UL 60335-2-2019'],
          ['radiometryCalibrationId', 'Radiometry calibration ID'],
          ['radiometryCalibrationInterval', 'Calibration interval'],
          ['cdscoBisStatus', 'CDSCO / BIS status (free text)'],
        ].map(([key, label]) => (
          <TextField
            key={key}
            size="small"
            label={label}
            value={certs[key] || ''}
            disabled={adminView}
            onChange={(e) => updateField(`deviceCerts.${key}`, e.target.value)}
            fullWidth
          />
        ))}
      </Box>

      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>
          Verification thresholds (tier × method × instrument)
        </Typography>
        {!adminView ? (
          <Button
            size="small"
            startIcon={<MdAdd />}
            onClick={addThreshold}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add threshold
          </Button>
        ) : null}
      </Box>

      {(settings.verificationThresholds || []).map((t, index) => (
        <Box
          key={t.id || index}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: `${theme.radius.md}px`,
            border: `1px solid ${COLORS.borderDefault}`,
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto' },
            alignItems: 'start',
          }}
        >
          <TextField
            select
            size="small"
            label="Risk tier"
            value={t.riskTier || 'general'}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'riskTier', e.target.value)}
          >
            {TIER_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Method"
            value={t.method || 'atp'}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'method', e.target.value)}
          >
            {METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Instrument ID"
            value={t.instrumentId || 'default'}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'instrumentId', e.target.value)}
          />
          {!adminView ? (
            <IconButton size="small" onClick={() => removeThreshold(index)} aria-label="Remove">
              <MdDelete />
            </IconButton>
          ) : (
            <span />
          )}
          <TextField
            size="small"
            type="number"
            label="Pass max"
            value={t.passMax ?? ''}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'passMax', e.target.value)}
          />
          <TextField
            size="small"
            type="number"
            label="Pass min"
            value={t.passMin ?? ''}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'passMin', e.target.value)}
          />
          <TextField
            size="small"
            label="Unit"
            value={t.unit || ''}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'unit', e.target.value)}
          />
          <TextField
            size="small"
            label="Source citation (required)"
            value={t.sourceCitation || ''}
            disabled={adminView}
            onChange={(e) => updateThreshold(index, 'sourceCitation', e.target.value)}
            sx={{ gridColumn: { md: '1 / -1' } }}
            fullWidth
          />
        </Box>
      ))}

      {!adminView ? (
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: `${theme.radius.md}px`,
            bgcolor: '#0f766e',
            '&:hover': { bgcolor: '#0d9488' },
          }}
        >
          {saving ? 'Saving…' : 'Save hygiene settings'}
        </Button>
      ) : null}
    </Paper>
  );
}
